import { useState } from "react";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { useDotLegalFileUploadDataMapping } from "../../common/components/dotLegalFileUploadTable/DotLegalFileUploadTable.hooks";
import { useStateUrlParams } from "../../common/hooks/useStateUrlParams";
import { isNullOrWhitespace } from "../../common/stringOperations";
import { useTranslation } from "../../localization/useTranslation";
import { ILegalEntityDocumentTabProps } from "./LegalEntityDocumentTab";
import { LegalEntityDocumentRow, SelectedLegalEntityDocument } from "./LegalEntityDocumentTab.types";
import { useParams } from "react-router";
import { EntityType } from "../../common/Common.types.ts";
import { useAddTaskMenuItem } from "../../common/components/addTaskMenuItem/AddTaskMenuItemContext.hooks.ts";
import { usePlanContext } from "../../auth/planProvider/PlanProvider.tsx";
import { useBestPracticeDialogsHook } from "@common/components/bestPracticeCheckDialog/BestPracticeCheckDialog.hooks.ts";
import { getAiQualityScanOverflowMenuItem } from "@common/components/bestPracticeCheckDialog/aiQualityScanOverflowMenuItem.ts";
import { useAiFeatureAccess } from "../../plan/aiFeatureGate/useAiFeatureAccess";

import { useDotLegalQuery, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { applyClientFilters, matchesDateFilter, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";
import { getUniqueFilterOptions, getUniqueNestedFilterOptions, sortByEnumValue, sortByName } from "@common/hooks/filterUtils.ts";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { DocumentStatus } from "../../documents/Documents.types";

// A document that reaches the entire group shows "entire group" instead of any names, so the group entity filter
// offers that as its own option rather than leaving those documents unmatchable.
const entireGroupFilterId = "allGroupEntities";

export function useLegalEntityDocumentTabDataMapping(props: ILegalEntityDocumentTabProps) {
    const queryClient = useDotLegalQueryClient();
    const { translateString } = useTranslation();
    const { downloadDocumentVersion } = useDotLegalFileUploadDataMapping();
    const [showAddEditDialog, setShowAddEditDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<SelectedLegalEntityDocument>();
    const [showDeleteDocumentDialog, setShowDeleteDocumentDialog] = useState(false);
    const [showAuditLogDialog, setShowAuditLogDialog] = useStateUrlParams("document-auditlog", null);
    const { permissions } = useUserContext();
    const { platformFeatures } = usePlanContext();
    const { showAiQualityAssurance, planActivated } = useAiFeatureAccess();
    const {
        bestPracticeCheckDocument,
        setBestPracticeCheckDocument,
        bestPracticeCheckDocumentToQualityAssurance,
        setBestPracticeCheckDocumentToQualityAssurance,
        bestPracticeCheckInitialResult,
        setBestPracticeCheckInitialResult,
        onCheckComplete,
    } = useBestPracticeDialogsHook();
    const { id } = useParams<{ id: string }>();
    const addTaskMenuItem = useAddTaskMenuItem();

    const url = `/LegalEntity/${props.legalEntityId}/documents`;
    let { isLoading, data } = useDotLegalQuery<Array<LegalEntityDocumentRow>>(url);

    const { selectFilters, booleanFilters, dateFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState(
        `legalEntity-${props.legalEntityId}-documents-filters`
    );
    const createdFilter = dateFilters.created ?? null;
    const qualityScanFilter = booleanFilters.qualityScan ?? "";

    const documents = applyClientFilters(
        data,
        [
            { id: "name", candidate: (doc) => doc.name },
            { id: "document", candidate: (doc) => doc.fileName ?? doc.link },
            { id: "type", candidate: (doc) => doc.documentType.id },
            { id: "status", candidate: (doc) => doc.status.toString() },
            { id: "owner", candidate: (doc) => doc.owner.id },
            { id: "groupEntities", candidate: (doc) => getGroupEntityFilterCandidates(doc) },
        ],
        selectFilters,
        {},
        filterOperators,
        (doc) => {
            if (!matchesDateFilter(createdFilter, doc.created)) {
                return false;
            }

            if (qualityScanFilter !== "") {
                return qualityScanFilter === (doc.aiQualityAssuranceResult != null);
            }

            return true;
        }
    );

    function getGroupEntityFilterCandidates(document: LegalEntityDocumentRow) {
        return document.allGroupEntities ? [entireGroupFilterId] : document.groupEntities;
    }

    function getGroupEntityFilterData() {
        const groupEntities = getUniqueNestedFilterOptions(
            data,
            (doc) => doc.groupEntities,
            (groupEntity) => groupEntity
        ).sort(sortByName);

        if ((data ?? []).some((doc) => doc.allGroupEntities)) {
            return [{ id: entireGroupFilterId, name: translateString("entireGroup") }, ...groupEntities];
        }

        return groupEntities;
    }

    function getFilterOptions(): Array<FilterOption> {
        const options: Array<FilterOption> = [
            {
                id: "name",
                label: translateString("name"),
                type: FilterOptionType.SelectField,
                value: selectFilters.name ?? [],
                fixedData: getUniqueFilterOptions(data, (doc) => doc.name).sort(sortByName),
            },
            {
                id: "document",
                label: translateString("document"),
                type: FilterOptionType.SelectField,
                value: selectFilters.document ?? [],
                fixedData: getUniqueFilterOptions(data, (doc) => doc.fileName ?? doc.link ?? undefined).sort(sortByName),
            },
            {
                id: "type",
                label: translateString("type"),
                type: FilterOptionType.SelectField,
                value: selectFilters.type ?? [],
                fixedData: getUniqueFilterOptions(
                    data,
                    (doc) => doc.documentType.name,
                    (doc) => doc.documentType.id
                ).sort(sortByName),
            },
            {
                id: "status",
                label: translateString("status"),
                type: FilterOptionType.SelectField,
                value: selectFilters.status ?? [],
                fixedData: getUniqueFilterOptions(
                    data,
                    (doc) => translateString(DocumentStatus[doc.status].toLowerCase()),
                    (doc) => doc.status.toString()
                ).sort(sortByEnumValue),
            },
            {
                id: "owner",
                label: translateString("owner"),
                type: FilterOptionType.SelectField,
                value: selectFilters.owner ?? [],
                fixedData: getUniqueFilterOptions(
                    data,
                    (doc) => doc.owner.name,
                    (doc) => doc.owner.id
                ).sort(sortByName),
            },
        ];

        if (!props.isGroupEntity) {
            options.push({
                id: "groupEntities",
                label: translateString("groupEntities"),
                type: FilterOptionType.SelectField,
                value: selectFilters.groupEntities ?? [],
                fixedData: getGroupEntityFilterData(),
            });
        }

        options.push({
            id: "created",
            label: translateString("created"),
            type: FilterOptionType.DateField,
            value: createdFilter ?? "",
            hideNoDate: true,
        });

        if (showAiQualityAssurance) {
            options.push({
                id: "qualityScan",
                label: translateString("aiQualityScan"),
                type: FilterOptionType.Boolean,
                value: qualityScanFilter,
            });
        }

        return withOperators(options, filterOperators);
    }

    const hasActiveFilters = Object.values(selectFilters).some((v) => v.length > 0) || createdFilter !== null || qualityScanFilter !== "";

    return {
        hasDocuments: isLoading || (data ?? []).length > 0,
        isLoading,
        data,
        documents: documents ?? [],
        getFilterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        hasActiveFilters,
        getMenuItems,
        showAddEditDialog,
        setShowAddEditDialog,
        selectedDocument,
        setSelectedDocument,
        refetchDocuments,
        showDeleteDocumentDialog,
        setShowDeleteDocumentDialog,
        showEditDialog,
        setShowEditDialog,
        showAuditLogDialog,
        setShowAuditLogDialog,
        bestPracticeCheckDocument,
        setBestPracticeCheckDocument,
        bestPracticeCheckDocumentToQualityAssurance,
        setBestPracticeCheckDocumentToQualityAssurance,
        bestPracticeCheckInitialResult,
        setBestPracticeCheckInitialResult,
        onCheckComplete,
    };

    function refetchDocuments() {
        queryClient.refetchQueries(url);
    }

    function getMenuItems(document: LegalEntityDocumentRow) {
        let menuItems = [];

        if (permissions.companyDocumentationPermissions.edit) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => {
                    setSelectedDocument({
                        documentId: document.documentId,
                        name: document.name,
                        legalEntityId: id,
                        isGroupEntity: props.isGroupEntity,
                    });
                    setShowEditDialog(true);
                },
            });
        }

        if (isNullOrWhitespace(document.fileName)) {
            menuItems.push({
                menuItem: translateString("openLink"),
                onClick: () => window.open(document.link, "_blank"),
            });
        } else {
            menuItems.push({
                menuItem: translateString("downloadDocument"),
                onClick: () => downloadDocumentVersion(document.documentId, document.dataId, document.fileName, EntityType.LegalEntityDocument, id!),
            });
        }

        if (permissions.companyDocumentationPermissions.delete) {
            menuItems.push({
                menuItem: translateString("delete"),
                onClick: () => {
                    setSelectedDocument({
                        documentId: document.documentId,
                        name: document.name,
                        legalEntityId: id,
                    });
                    setShowDeleteDocumentDialog(true);
                },
            });
        }

        addTaskMenuItem.addCreateTaskMenuItem(menuItems, {
            fromEntityId: document.documentId!,
            fromEntityType: EntityType.LegalEntityDocument,
            toEntityType: EntityType.Task,
        });

        if (permissions.canAccessAuditLog) {
            menuItems.push({
                menuItem: translateString("auditlog"),
                onClick: () => {
                    setShowAuditLogDialog(document.legalEntityDocumentId);
                },
                planMenuItemInfo: {
                    hasAccess: platformFeatures.auditLog,
                    menuName: translateString("auditlog"),
                    tooltip: translateString("upgradePrivacyPlanToday"),
                },
            });
        }

        if (showAiQualityAssurance && !isNullOrWhitespace(document.fileName)) {
            menuItems.push(
                getAiQualityScanOverflowMenuItem(
                    () => {
                        if (document.aiQualityAssuranceResult) {
                            setBestPracticeCheckDocument({ documentId: document.documentId });
                        } else {
                            setBestPracticeCheckDocumentToQualityAssurance({ documentId: document.documentId });
                        }
                    },
                    planActivated,
                    document.isTooLargeForAi,
                    translateString
                )
            );
        }

        return menuItems;
    }
}
